<template>
  <form class="max-w-5xl" @submit.prevent="emit('save')">
    <div class="grid grid-cols-2 gap-4">
      <div class="flex items-center">
        <input v-model="slide.isFinalSlide" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500">
        <label class="ml-2 block text-sm text-gray-900">Final Slide</label>
      </div>

      <div class="flex items-center">
        <input v-model="slide.isCategorySlide" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500">
        <label class="ml-2 block text-sm text-gray-900">Is Category Slide</label>
      </div>
    </div>

    <div class="mt-10 mb-40 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6" :class="{ 'opacity-30 disabled': slide.deletedAt || slide.isEnabled === false }">
      <form-input v-if="!slide.isFinalSlide" v-model.trim="slide.title" label="Title" class="md:col-span-4" :error-msg="result.title.$messages.join(' | ')" />

      <form-textarea
        v-if="!slide.isFinalSlide"
        v-model="slide.description"
        label="Description"
        class="col-span-full"
        :error-msg="result.description.$messages.join(' | ')"
      />

      <form-input
        v-if="!slide.isFinalSlide"
        v-model.trim="slide.question"
        label="Question"
        class="md:col-span-4"
        :error-msg="result.question.$messages.join(' | ')"
      />

      <form-input
        v-model.number="slide.order"
        label="Order"
        class="md:col-span-1"
        type="number"
        :range="[1, 999]"
        :error-msg="result.order.$messages.join(' | ')"
      />

      <div v-if="!slide.isFinalSlide" class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-700 mb-2">Selection Type</label>
        <select v-model="slide.selectionType" class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm">
          <option value="multiple">
            Multiple Selection
          </option>
          <option value="single">
            Single Selection
          </option>
        </select>
      </div>

      <custom-form-tags
        v-if="!slide.isFinalSlide"
        v-model="slide.checkboxes"
        label="Checkboxes"
        class="col-span-full"
        :error-msg="result.checkboxes.$messages.join(' | ')"
      />

      <!-- Category Slide Configuration -->
      <template v-if="slide.isCategorySlide && !slide.isFinalSlide">
        <div class="col-span-full">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            Category Mapping Configuration
          </h3>
          <p class="text-sm text-gray-600 mb-4">
            Set up a mapping between user response options and program categories in the system.
          </p>
        </div>

        <div class="col-span-full">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-gray-700">Category Mapping (JSON)</label>
            <button
              type="button"
              class="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              @click="setDefaultCategoryMapping"
            >
              Set Default Values
            </button>
          </div>
          <textarea
            v-model="slide.categoryMapping"
            class="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm font-mono placeholder:text-gray-400"
            rows="8"
            placeholder="{
  &quot;3D Artist&quot;: &quot;3D&quot;,
  &quot;Data Analyst&quot;: &quot;Data Analytics&quot;,
  &quot;Architect&quot;: &quot;Architecture & Engineering&quot;,
  &quot;Multimedia Designer&quot;: &quot;Audio & Music&quot;,
  &quot;Game Developer&quot;: &quot;Simulation&quot;
}"
          />
          <p class="mt-1 text-xs text-gray-500">
            Format: {"Name in checkbox": "Name of category in the system"}
          </p>
          <div v-if="categoryMappingError" class="mt-2 text-sm text-red-600">
            {{ categoryMappingError }}
          </div>
        </div>
      </template>

      <!-- Final Slide Configuration -->
      <template v-if="slide.isFinalSlide">
        <div class="col-span-full">
          <h3 class="text-lg font-medium text-gray-900 mb-4">
            Final Slide Configuration
          </h3>
        </div>

        <form-input v-model.trim="slide.finalSlideConfig.title" label="Final Slide Title" class="md:col-span-4" />

        <form-textarea v-model="slide.finalSlideConfig.description" label="Final Slide Description" class="col-span-full" />

        <form-textarea v-model="slide.finalSlideConfig.shortDescription" label="Final Slide Short Description" class="col-span-full" />

        <form-image-upload v-model="slide.finalSlideConfig.image" label="Final Slide Image" class="col-span-2" />

        <div class="col-span-full">
          <h4 class="text-md font-medium text-gray-900 mb-3">
            Visibility Settings
          </h4>
          <div class="grid grid-cols-2 gap-4">
            <div class="flex items-center">
              <input
                v-model="slide.finalSlideConfig.showTelegramButton"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              >
              <label class="ml-2 block text-sm text-gray-900">Show Telegram Button</label>
            </div>
            <div class="flex items-center">
              <input
                v-model="slide.finalSlideConfig.showWhatsappButton"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              >
              <label class="ml-2 block text-sm text-gray-900">Show WhatsApp Button</label>
            </div>
            <div class="flex items-center">
              <input
                v-model="slide.finalSlideConfig.showTelegramQR"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              >
              <label class="ml-2 block text-sm text-gray-900">Show Telegram QR Code</label>
            </div>
            <div class="flex items-center">
              <input
                v-model="slide.finalSlideConfig.showWhatsappQR"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              >
              <label class="ml-2 block text-sm text-gray-900">Show WhatsApp QR Code</label>
            </div>
          </div>
        </div>
      </template>

      <form-image-upload
        v-if="!slide.isFinalSlide"
        v-model="slide.image"
        label="Image"
        class="col-span-2"
        :error-msg="(result.image?.$messages || []).join(' | ')"
      />
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue';
import FormInput from './FormInput.vue';
import FormTextarea from './FormTextarea.vue';
import CustomFormTags from './CustomFormTags.vue';
import FormImageUpload from './FormImageUpload.vue';
import useValidate from 'vue-tiny-validate';

const slide = defineModel('slide', {
  type: Object,
  default: () => ({
    title: '',
    description: '',
    question: '',
    checkboxes: [],
    image: null,
    order: 0,
    isActive: true,
    selectionType: 'multiple',
    isFinalSlide: false,
    isCategorySlide: false,
    categoryMapping:
      '{\n  "3D Artist": "3D",\n  "Data Analyst": "Data Analytics",'
      + '\n  "Architect": "Architecture & Engineering",\n  "Multimedia Designer": "Audio & Music",'
      + '\n  "Game Developer": "Simulation"\n}',
    finalSlideConfig: {
      title: '',
      description: '',
      shortDescription: '',
      image: null,
      showTelegramButton: true,
      showWhatsappButton: true,
      showTelegramQR: true,
      showWhatsappQR: true
    }
  })
});

const emit = defineEmits([ 'save' ]);

defineExpose({
  onValidate,
  getResult
});

const rules = reactive({
  title: {
    name: 'required',
    test: value => slide.value.isFinalSlide || Boolean(value),
    message: 'Title must not be empty.'
  },
  description: {
    name: 'required',
    test: value => slide.value.isFinalSlide || Boolean(value),
    message: 'Description must not be empty.'
  },
  question: {
    name: 'required',
    test: value => slide.value.isFinalSlide || Boolean(value),
    message: 'Question must not be empty.'
  },
  checkboxes: {
    name: 'required',
    test: value => slide.value.isFinalSlide || (Array.isArray(value) && value.length > 0),
    message: 'Checkboxes must not be empty.'
  },
  order: {
    name: 'required',
    test: value => value !== null && value !== undefined,
    message: 'Order must not be empty.'
  }
});

const options = reactive({
  autoTest: true
});

const { result } = useValidate(slide, rules, options);

const categoryMappingError = computed(() => {
  if (!slide.value.isCategorySlide || !slide.value.categoryMapping) return null;

  try {
    const parsed = JSON.parse(slide.value.categoryMapping);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'Mapping must be an object';
    }
    return null;
  } catch (error) {
    return `Invalid JSON format: ${error}`;
  }
});

async function onValidate() {
  //For the final slide, we skip validation of regular fields
  if (slide.value.isFinalSlide) {
    // For the final slide, only validate the order field
    const orderValid = await rules.order.test(slide.value.order);
    return orderValid;
  }

  if (slide.value.isCategorySlide && categoryMappingError.value) {
    return false;
  }

  // For simple slide, use full validation
  return await result.value.$test();
}

function getResult() {
  return result.value;
}

// Clean up unnecessary fields when switching to final slide
watch(
  () => slide.value.isFinalSlide,
  isFinal => {
    if (isFinal) {
      slide.value.title = '';
      slide.value.description = '';
      slide.value.question = '';
      slide.value.checkboxes = [];
      slide.value.image = null;
      slide.value.selectionType = 'multiple';
      slide.value.isCategorySlide = false;
    }
  }
);

// Reset category slide when final slide is enabled
watch(
  () => slide.value.isCategorySlide,
  isCategorySlide => {
    if (isCategorySlide) {
      slide.value.isFinalSlide = false;
    }
  }
);

function setDefaultCategoryMapping() {
  const defaultMapping = {
    'Productivity': 'Productivity',
    '3D Artist': '3D',
    'Audio & Music': 'Audio & Music',
    'Architect': 'Architecture & Engineering',
    'Simulation': 'Simulation',
    'Game Developer': 'Game Development',
    'Graphics Designer': 'Graphics',
    'Data Analyst': 'Data Analytics',
    'Video Editor': 'Video & Motion',
    'Publisher': 'Publishing'
  };

  slide.value.categoryMapping = JSON.stringify(defaultMapping, null, 2);
}
</script>
